import { useCallback } from 'react'
import { parsePath, useLocation, useNavigate } from 'react-router-dom'

import { mergeSearch, mergeShellSearch } from '@/lib/shellSearch'

interface NavigateOptions {
  replace?: boolean
  state?: unknown
}

interface AppNavigate {
  /**
   * Cross-tab navigation. Carries the shell keys (agent/conversationId/view)
   * from the current URL; the current tab's task query stays behind. `to` may
   * bring its own query and hash — its own params always win.
   */
  toTask: (to: string, options?: NavigateOptions) => void
  /**
   * Same-tab sub-view switch. Preserves the ENTIRE current search string and
   * the current hash — the tab keeps its own task query and anchor while its
   * pathname changes. A `to` that brings its own query or hash wins.
   */
  withinTask: (to: string, options?: NavigateOptions) => void
}

/**
 * The app's two navigation intents. They are separate because copying the whole
 * query string on every navigation leaks one tab's task filters onto the next,
 * and copying nothing drops the chat context the shell keys carry.
 */
export function useAppNavigate(): AppNavigate {
  const navigate = useNavigate()
  const location = useLocation()

  const toTask = useCallback(
    (to: string, options?: NavigateOptions) => {
      const path = parsePath(to)
      navigate(
        {
          pathname: path.pathname ?? location.pathname,
          search: mergeShellSearch(path.search ?? '', location.search),
          hash: path.hash ?? '',
        },
        options
      )
    },
    [navigate, location.pathname, location.search]
  )

  const withinTask = useCallback(
    (to: string, options?: NavigateOptions) => {
      const path = parsePath(to)
      navigate(
        {
          pathname: path.pathname ?? location.pathname,
          // Target's own params win over the preserved task query.
          search: mergeSearch(path.search ?? '', location.search),
          // Same tab, same document: keep the current anchor unless the target
          // names one of its own.
          hash: path.hash || location.hash,
        },
        options
      )
    },
    [navigate, location.pathname, location.search, location.hash]
  )

  return { toTask, withinTask }
}
